import type { BrandUploadPurpose } from "../types/brand.types";

// 리사이즈 대상 이미지 자산만(제안서 PDF 제외).
type BrandImagePurpose = Exclude<BrandUploadPurpose, "BRAND_PROPOSAL">;

// purpose별 타겟 긴 변(px). 백엔드 FileService._brandImageMaxPx와 동일하게 유지.
const BRAND_IMAGE_MAX_PX: Record<BrandImagePurpose, number> = {
  BRAND_LOGO_HEADER: 400,
  BRAND_LOGO_AUTH: 400,
  BRAND_FAVICON: 256,
  BRAND_OG: 1200,
};

const RESIZABLE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/bmp",
]);

// 업로드 전 Canvas 리사이즈 — purpose별 타겟 긴 변으로 비율 유지 축소.
// 이미 타겟 이하이거나 리사이즈 불가(타입/디코드 실패) 시 원본 File 반환.
// 백엔드(SkiaSharp)가 안전망으로 동일 규격 재검증하므로, 실패해도 안전.
export async function resizeBrandImage(
  file: File,
  purpose: BrandUploadPurpose,
): Promise<File> {
  if (purpose === "BRAND_PROPOSAL") return file; // PDF는 리사이즈 대상 아님
  if (!RESIZABLE_TYPES.has(file.type)) return file;

  const maxPx = BRAND_IMAGE_MAX_PX[purpose];

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // 디코드 실패 → 원본 (백엔드가 처리)
  }

  try {
    const longerSide = Math.max(bitmap.width, bitmap.height);
    if (longerSide <= maxPx) return file; // 타겟 이하 → 원본 유지

    const ratio = maxPx / longerSide;
    const newWidth = Math.max(1, Math.round(bitmap.width * ratio));
    const newHeight = Math.max(1, Math.round(bitmap.height * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);

    // PNG는 무손실 유지, 그 외는 JPEG 품질 0.9
    const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outType, 0.9),
    );
    if (!blob) return file;

    return new File([blob], file.name, {
      type: outType,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
}
